import type { TextContentModifierValue } from "schemas/generated/fileModifiers";

import React, { useEffect, useRef } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useIsSelectedElementDynamic } from "@v2/features/modifiers/hooks/useIsSelectedElementDynamic";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { useDefaultSelectedElementDesignation } from "@v2/features/modifiers/store/ModifiersStoreContext";
import { parseTextFormatting } from "@v2/features/modifiers/utils";
import {
  Bold,
  Italic,
  Strikethrough,
  Underline as UnderlineIcon,
} from "lucide-react";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

import { DynamicElementTooltip } from "./AssetModifier";

interface ContentModifierProps {
  onChange: (modifiers: TextContentModifierValue[]) => void;
}

const ContentModifierComponent: React.FC<ContentModifierProps> = ({
  onChange,
}) => {
  const defaultDesignation = useDefaultSelectedElementDesignation();
  const { isDynamic, isDynamicProduct, hasDataRidChildren } =
    useIsSelectedElementDynamic();
  // NOTE (Kurt, 2025-12-11): Prevents feedback loops when programmatically updating TipTap content
  const isUpdatingRef = useRef(false);
  // NOTE (Kurt, 2025-12-11): Tracks the current element to detect element switches
  const lastElementIdRef = useRef<string | null>(null);

  // Extract current value from selected element
  const currentContent = useOptimisticModifierValue({
    modifierType: { type: "content", value: "", className: "" },
    extractor: (elementDesignation) => {
      return elementDesignation.element.innerHtml ?? "";
    },
  });

  const currentClassName = useOptimisticModifierValue({
    modifierType: { type: "content", value: "", className: "" },
    extractor: (elementDesignation) => {
      return elementDesignation.element.props?.className ?? "";
    },
  });

  const currentValue = React.useMemo(() => {
    if (currentContent === undefined) {
      return null;
    }
    return {
      content: currentContent,
      className: currentClassName ?? "",
    };
  }, [currentContent, currentClassName]);

  // NOTE (Kurt, 2025-12-11): Debounce server requests to avoid flooding the server
  // during rapid typing. The user sees immediate feedback in TipTap, while the
  // iframe preview and server sync happen after the debounce period.
  const debouncedOnChange = useDebouncedCallback(
    (modifiers: TextContentModifierValue[]) => {
      onChange(modifiers);
    },
    300,
  );

  const handleContentChange = (newContent: string, newClassName?: string) => {
    if (isUpdatingRef.current) {
      return;
    }

    debouncedOnChange([
      {
        type: "content" as const,
        value: newContent,
        className: newClassName ?? "",
      },
    ]);
  };

  /**
   * Apply formatting to selected text, or all text if nothing is selected
   */
  const applyFormatting = (formatCommand: () => any) => {
    if (!editor) {
      return;
    }

    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      // Apply to selected text
      formatCommand();
    } else {
      // Apply to all text
      editor.chain().focus().selectAll().run();
      formatCommand();
    }
  };

  /**
   * Extract content and formatting from TipTap editor
   */
  const extractContentAndFormatting = (editor: any) => {
    // Get plain text content
    const textContent = editor.getText();

    // Use TipTap's built-in methods to check active formatting
    const formattingClasses: string[] = [];

    [
      { type: "bold", className: "font-bold" },
      { type: "italic", className: "italic" },
      { type: "underline", className: "underline" },
      { type: "strike", className: "line-through" },
    ].forEach(({ type, className }) => {
      if (editor.isActive(type)) {
        formattingClasses.push(className);
      }
    });

    const className = formattingClasses.join(" ");
    return { textContent, className };
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Only specify the ones we want to disable
        // bold, italic, and strike are enabled by default
        heading: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
    ],
    content: currentValue?.content ?? "",
    editable: !isDynamic && !hasDataRidChildren,
    onUpdate: ({ editor }) => {
      if (isUpdatingRef.current) {
        return;
      }
      const { textContent, className } = extractContentAndFormatting(editor);
      handleContentChange(textContent, className);
    },
    editorProps: {
      attributes: {
        class: "content-modifier-editor text-xs break-words",
      },
    },
  });

  // Note (Nick, 12/18/2025): Update editor editable state when isDynamic or hasDataRidChildren changes
  useEffect(() => {
    if (!editor) {
      return;
    }

    const shouldBeEditable = !isDynamic && !hasDataRidChildren;

    // Only update if the editable state actually needs to change
    if (editor.isEditable !== shouldBeEditable) {
      // Set isUpdatingRef to prevent onUpdate from triggering modifier requests
      isUpdatingRef.current = true;

      editor.setEditable(shouldBeEditable);

      // Reset the flag after a brief delay to allow the editor to settle
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [editor, isDynamic, hasDataRidChildren]);

  // NOTE (Kurt, 2025-12-11): Syncs TipTap editor content with the currently selected element.
  // Handles element switching by canceling pending debounced requests and forcing content refresh.
  // Avoids overwriting user input by skipping updates while the editor is focused (unless
  // the element changed, in which case we must show the new element's content).
  useEffect(() => {
    if (!editor || !currentValue) {
      return;
    }

    const newElementId = defaultDesignation?.element.stableElementId ?? null;
    const elementChanged = newElementId !== lastElementIdRef.current;

    // NOTE (Kurt, 2025-12-11): When the selected element changes, we must:
    // 1. Cancel any pending debounced requests (they were for the old element)
    // 2. Update our tracking ref
    // 3. Force sync TipTap with the new element's content
    if (elementChanged) {
      debouncedOnChange.cancel();
      lastElementIdRef.current = newElementId;
    }

    // NOTE (Kurt, 2025-12-11): Don't overwrite TipTap while user is typing,
    // UNLESS we switched to a different element (must show new element's content)
    if (editor.isFocused && !elementChanged) {
      return;
    }

    const newContent = currentValue.content ?? "";
    const newClassName = currentValue.className ?? "";
    const currentEditorContent = editor.getText();

    // NOTE (Kurt, 2025-12-11): Update TipTap content if content changed OR if we switched elements.
    // The element switch check is important because two elements may have identical content,
    // but we still need to reset TipTap's internal state (selection, formatting marks, etc.).
    if (currentEditorContent !== newContent || elementChanged) {
      isUpdatingRef.current = true;

      // Update content
      editor.commands.setContent(newContent);

      // Apply existing formatting from className using the common utility
      const styleOverrides = defaultDesignation?.element.styleOverrides ?? {};
      const existingFormatting = parseTextFormatting(
        styleOverrides,
        newClassName,
      );

      // Clear all formatting first
      editor
        .chain()
        .selectAll()
        .unsetBold()
        .unsetItalic()
        .unsetUnderline()
        .unsetStrike()
        .run();

      // Apply existing formatting
      const chain = editor.chain().selectAll();

      if (existingFormatting.bold) {
        chain.setBold();
      }
      if (existingFormatting.italic) {
        chain.setItalic();
      }
      if (existingFormatting.underline) {
        chain.setUnderline();
      }
      if (existingFormatting.strikethrough) {
        chain.setStrike();
      }

      chain.run();

      // Reset the flag after a brief delay to allow the editor to settle
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  }, [currentValue, editor, defaultDesignation, debouncedOnChange]);

  if (!editor) {
    return null;
  }

  const isDisabled = isDynamic || hasDataRidChildren;

  const formattingToolbar = (
    <div className="flex gap-1 px-2 py-1 bg-surface border-b border-border items-center">
      <Button
        icon
        variant="tertiary"
        size="sm"
        disabled={isDisabled}
        onClick={() => {
          return applyFormatting(() => {
            return editor.chain().focus().toggleBold().run();
          });
        }}
        UNSAFE_className={twMerge(
          !isDisabled && editor.isActive("bold") && "bg-neutral-soft",
        )}
      >
        <Bold />
      </Button>
      <Button
        icon
        variant="tertiary"
        size="sm"
        disabled={isDisabled}
        onClick={() => {
          return applyFormatting(() => {
            return editor.chain().focus().toggleItalic().run();
          });
        }}
        UNSAFE_className={twMerge(
          !isDisabled && editor.isActive("italic") && "bg-neutral-soft",
        )}
      >
        <Italic />
      </Button>
      <Button
        icon
        variant="tertiary"
        size="sm"
        disabled={isDisabled}
        onClick={() => {
          return applyFormatting(() => {
            return editor.chain().focus().toggleUnderline().run();
          });
        }}
        UNSAFE_className={twMerge(
          !isDisabled && editor.isActive("underline") && "bg-neutral-soft",
        )}
      >
        <UnderlineIcon />
      </Button>
      <Button
        icon
        variant="tertiary"
        size="sm"
        disabled={isDisabled}
        onClick={() => {
          return applyFormatting(() => {
            return editor.chain().focus().toggleStrike().run();
          });
        }}
        UNSAFE_className={twMerge(
          !isDisabled && editor.isActive("strike") && "bg-neutral-soft",
        )}
      >
        <Strikethrough />
      </Button>
    </div>
  );

  const editorContent = (
    <div
      className="p-2 min-h-[80px] bg-white cursor-text"
      onClick={() => {
        if (!isDisabled && !editor?.isFocused) {
          editor?.commands.focus("end");
        }
      }}
    >
      <EditorContent editor={editor} />
    </div>
  );

  const editorUI = (
    <div className="border border-border rounded-lg overflow-hidden">
      {formattingToolbar}
      {editorContent}
    </div>
  );

  if (isDisabled) {
    return (
      <DynamicElementTooltip
        isDynamic={isDynamic}
        isDynamicProduct={isDynamicProduct}
        hasDataRidChildren={hasDataRidChildren}
      >
        <div className="content-modifier opacity-50 cursor-not-allowed">
          <div className="pointer-events-none">{editorUI}</div>
        </div>
      </DynamicElementTooltip>
    );
  }

  return <div className="content-modifier">{editorUI}</div>;
};

export default ContentModifierComponent;
